/**
 * AdvancedDebugPanel — Slide-out panel for advanced debug mode (DEC-050)
 *
 * Three tabs:
 * 1. WS Events — real-time log of all WebSocket traffic (in/out)
 * 2. Server Logs — streamed Python logger output
 * 3. Debug Events — ContentAnalyzer, ContextObserver, etc.
 *
 * Each tab has filters, auto-scroll, clear, and copy support.
 */

import { useState, useRef, useEffect, useCallback, useMemo } from 'react'
import { useChatStore } from '../../stores/chatStore'
import type { WsLogEntry, ServerLogEntry, DebugEvent } from '../../stores/chatStore'

type TabId = 'ws' | 'server' | 'debug' | 'perf' | 'pipeline'

// Filter state per tab
interface WsFilters {
  direction: 'all' | 'in' | 'out'
  typeFilter: string  // substring match on type
}

interface ServerFilters {
  levels: Set<string>
  loggerFilter: string  // substring match
}

interface DebugFilters {
  agentFilter: string  // substring match on agent name
  messageIdFilter: string  // exact match
}

const LEVEL_COLORS: Record<string, string> = {
  DEBUG: 'text-neutral-400',
  INFO: 'text-blue-400',
  WARNING: 'text-yellow-400',
  ERROR: 'text-red-400',
}

const LEVEL_BG: Record<string, string> = {
  DEBUG: 'bg-neutral-700',
  INFO: 'bg-blue-900/50',
  WARNING: 'bg-yellow-900/50',
  ERROR: 'bg-red-900/50',
}

function formatTime(iso: string): string {
  try {
    const d = new Date(iso)
    return d.toLocaleTimeString('en-GB', { hour12: false }) + '.' + String(d.getMilliseconds()).padStart(3, '0')
  } catch {
    return iso
  }
}

function copyToClipboard(text: string) {
  navigator.clipboard.writeText(text).catch(() => {})
}

// ── WS Event Row ──────────────────────────────────────────────
function WsEventRow({ entry }: { entry: WsLogEntry }) {
  const [expanded, setExpanded] = useState(false)

  return (
    <div
      className="px-3 py-1.5 border-b border-neutral-800 hover:bg-neutral-800/50 cursor-pointer text-xs font-mono"
      onClick={() => setExpanded(!expanded)}
    >
      <div className="flex items-center gap-2">
        <span className="text-neutral-500 w-20 shrink-0">{formatTime(entry.timestamp)}</span>
        <span className={`w-4 shrink-0 ${entry.direction === 'out' ? 'text-green-400' : 'text-blue-400'}`}>
          {entry.direction === 'out' ? '\u2192' : '\u2190'}
        </span>
        <span className="text-yellow-300 w-28 shrink-0 truncate">{entry.type}</span>
        <span className="text-neutral-400 truncate flex-1">{entry.summary}</span>
        <button
          onClick={(e) => { e.stopPropagation(); copyToClipboard(JSON.stringify(entry.rawData, null, 2)) }}
          className="text-neutral-600 hover:text-neutral-300 shrink-0"
          title="Copy JSON"
        >
          \u2398
        </button>
      </div>
      {expanded && (
        <pre className="mt-1 text-neutral-400 whitespace-pre-wrap text-[10px] max-h-48 overflow-auto bg-neutral-900 rounded p-2">
          {JSON.stringify(entry.rawData, null, 2)}
        </pre>
      )}
    </div>
  )
}

// ── Server Log Row ────────────────────────────────────────────
function ServerLogRow({ entry }: { entry: ServerLogEntry }) {
  const [expanded, setExpanded] = useState(false)
  const isMultiline = entry.message.includes('\n')
  const firstLine = isMultiline ? entry.message.split('\n')[0] : entry.message

  return (
    <div
      className={`px-3 py-1 border-b border-neutral-800 text-xs font-mono ${LEVEL_BG[entry.level] || ''} ${entry.level === 'ERROR' ? 'border-l-2 border-l-red-500' : ''} cursor-pointer hover:bg-neutral-800/50`}
      onClick={() => setExpanded(!expanded)}
    >
      <div className="flex items-center gap-2">
        <span className="text-neutral-500 w-20 shrink-0">{formatTime(entry.timestamp)}</span>
        <span className={`w-14 shrink-0 font-bold ${LEVEL_COLORS[entry.level] || 'text-neutral-400'}`}>
          {entry.level}
        </span>
        <span className="text-neutral-500 w-32 shrink-0 truncate">{entry.logger}</span>
        <span className="text-neutral-300 flex-1 truncate">{firstLine}</span>
        {isMultiline && (
          <span className="text-neutral-600 shrink-0 text-[10px]">{expanded ? '▼' : '▶'}</span>
        )}
        <button
          onClick={(e) => { e.stopPropagation(); copyToClipboard(`[${entry.level}] ${entry.logger}: ${entry.message}`) }}
          className="text-neutral-600 hover:text-neutral-300 shrink-0"
          title="Copy"
        >
          \u2398
        </button>
      </div>
      {expanded && (
        <pre className="mt-1 text-neutral-400 whitespace-pre-wrap text-[10px] max-h-48 overflow-auto bg-neutral-900 rounded p-2">
          {entry.message}
        </pre>
      )}
    </div>
  )
}

// ── Debug Event Row ───────────────────────────────────────────
function DebugEventRow({ entry }: { entry: DebugEvent }) {
  const [expanded, setExpanded] = useState(false)

  return (
    <div
      className="px-3 py-1.5 border-b border-neutral-800 hover:bg-neutral-800/50 cursor-pointer text-xs font-mono"
      onClick={() => setExpanded(!expanded)}
    >
      <div className="flex items-center gap-2">
        <span className="text-neutral-500 w-20 shrink-0">{formatTime(entry.timestamp)}</span>
        <span className="shrink-0">{entry.icon}</span>
        <span className="text-purple-300 w-28 shrink-0 truncate">{entry.agent}</span>
        <span className="text-neutral-500 w-12 shrink-0">msg:{entry.messageId}</span>
        <span className="text-neutral-400 truncate flex-1">
          {JSON.stringify(entry.data).slice(0, 80)}
        </span>
        <button
          onClick={(e) => { e.stopPropagation(); copyToClipboard(JSON.stringify(entry.data, null, 2)) }}
          className="text-neutral-600 hover:text-neutral-300 shrink-0"
          title="Copy JSON"
        >
          \u2398
        </button>
      </div>
      {expanded && (
        <pre className="mt-1 text-neutral-400 whitespace-pre-wrap text-[10px] max-h-48 overflow-auto bg-neutral-900 rounded p-2">
          {JSON.stringify(entry.data, null, 2)}
        </pre>
      )}
    </div>
  )
}

// ── Performance helpers ───────────────────────────────────────

interface PerfEntry {
  messageId: number
  timestamp: string
  content_analysis_ms?: number
  generation_ms?: number
  tts_generation_ms?: number
  tts_duration_ms?: number
  video_generation_ms?: number
  total_ms?: number
}

function formatMs(ms: number | undefined): string {
  if (ms == null) return '\u2014'
  if (ms < 1000) return `${ms}ms`
  return `${(ms / 1000).toFixed(1)}s`
}

function PerfTab({ perfEntries, debugEvents }: { perfEntries: PerfEntry[]; debugEvents: DebugEvent[] }) {
  // C10: Session therapeutic metrics
  const therapeuticMetrics = useMemo(() => {
    const emotions: Record<string, number> = {}
    let crisisCount = 0
    let escalationCount = 0
    const models: Record<string, number> = {}
    let mismatchCount = 0

    for (const evt of debugEvents) {
      if (evt.agent === 'ContentAnalyzer') {
        const ed = evt.data.emotion_detail as Record<string, unknown> | undefined
        const emotion = (ed?.primary as string) || (evt.data.emotion as string)
        if (emotion) {
          const key = emotion.split(' ')[0].toLowerCase()
          emotions[key] = (emotions[key] || 0) + 1
        }
        if (evt.data.crisis && evt.data.crisis !== 'none') crisisCount++
      }
      if (evt.agent === 'Orchestrator') {
        if (evt.data.escalated) escalationCount++
        const model = (evt.data.model as string) || 'sonnet'
        models[model] = (models[model] || 0) + 1
      }
      if (evt.agent === 'VoiceAnalysis') {
        const mc = evt.data.mismatch_count as number | undefined
        if (mc && mc > 0) mismatchCount += mc
      }
    }

    return { emotions, crisisCount, escalationCount, models, mismatchCount }
  }, [debugEvents])

  const averages = useMemo(() => {
    if (perfEntries.length === 0) return null
    const sum = (key: keyof PerfEntry) => {
      const vals = perfEntries.map(e => e[key]).filter((v): v is number => typeof v === 'number' && v > 0)
      return vals.length > 0 ? { avg: Math.round(vals.reduce((a, b) => a + b, 0) / vals.length), count: vals.length } : null
    }
    return {
      content_analysis: sum('content_analysis_ms'),
      generation: sum('generation_ms'),
      tts: sum('tts_generation_ms'),
      total: sum('total_ms'),
    }
  }, [perfEntries])

  if (perfEntries.length === 0) {
    return <div className="text-neutral-600 text-xs text-center py-8">No performance data yet. Send a message to see timing.</div>
  }

  const hasMetrics = Object.keys(therapeuticMetrics.emotions).length > 0 || therapeuticMetrics.crisisCount > 0

  return (
    <div className="text-xs font-mono">
      {/* Therapeutic Metrics (C10) */}
      {hasMetrics && (
        <div className="px-3 py-2 border-b border-neutral-700 bg-neutral-800/30">
          <div className="text-neutral-400 font-bold mb-1">Session Metrics</div>
          <div className="flex flex-wrap gap-x-3 gap-y-1 text-neutral-300">
            {Object.keys(therapeuticMetrics.emotions).length > 0 && (
              <span>
                <span className="text-neutral-500">Emotions: </span>
                {Object.entries(therapeuticMetrics.emotions)
                  .sort(([, a], [, b]) => b - a)
                  .map(([e, c]) => `${e}(${c})`)
                  .join(', ')}
              </span>
            )}
            <span>
              <span className="text-neutral-500">Crisis: </span>
              <span className={therapeuticMetrics.crisisCount > 0 ? 'text-red-400' : ''}>{therapeuticMetrics.crisisCount}</span>
            </span>
            <span>
              <span className="text-neutral-500">Escalations: </span>
              <span className={therapeuticMetrics.escalationCount > 0 ? 'text-yellow-400' : ''}>{therapeuticMetrics.escalationCount}</span>
            </span>
            {therapeuticMetrics.mismatchCount > 0 && (
              <span>
                <span className="text-neutral-500">Mismatches: </span>
                <span className="text-orange-400">{therapeuticMetrics.mismatchCount}</span>
              </span>
            )}
            {Object.keys(therapeuticMetrics.models).length > 0 && (
              <span>
                <span className="text-neutral-500">Models: </span>
                {Object.entries(therapeuticMetrics.models).map(([m, c]) => `${m}(${c})`).join(', ')}
              </span>
            )}
          </div>
        </div>
      )}
      {/* Averages */}
      {averages && (
        <div className="px-3 py-2 border-b border-neutral-700 bg-neutral-800/50">
          <div className="text-neutral-400 font-bold mb-1">Session Averages ({perfEntries.length} msgs)</div>
          <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 text-neutral-300">
            {averages.content_analysis && (
              <><span className="text-neutral-500">Analysis:</span><span>{formatMs(averages.content_analysis.avg)}</span></>
            )}
            {averages.generation && (
              <><span className="text-neutral-500">LLM:</span><span>{formatMs(averages.generation.avg)}</span></>
            )}
            {averages.tts && (
              <><span className="text-neutral-500">TTS:</span><span>{formatMs(averages.tts.avg)} ({averages.tts.count})</span></>
            )}
            {averages.total && (
              <><span className="text-neutral-500">Total:</span><span className="text-blue-400 font-bold">{formatMs(averages.total.avg)}</span></>
            )}
          </div>
        </div>
      )}
      {/* Per-message table */}
      <div className="px-3 py-1 border-b border-neutral-700 bg-neutral-800/30">
        <div className="flex items-center gap-1 text-neutral-500 text-[10px]">
          <span className="w-6">#</span>
          <span className="w-14">Anlys</span>
          <span className="w-14">LLM</span>
          <span className="w-14">TTS</span>
          <span className="w-14">Video</span>
          <span className="w-14 text-blue-400">Total</span>
        </div>
      </div>
      {[...perfEntries].reverse().map((entry, idx) => (
        <div key={`${entry.messageId}-${entry.timestamp}`} className="px-3 py-1 border-b border-neutral-800 hover:bg-neutral-800/50">
          <div className="flex items-center gap-1 text-neutral-300">
            <span className="w-6 text-neutral-600">{perfEntries.length - idx}</span>
            <span className="w-14">{formatMs(entry.content_analysis_ms)}</span>
            <span className="w-14">{formatMs(entry.generation_ms)}</span>
            <span className="w-14">{formatMs(entry.tts_generation_ms)}</span>
            <span className="w-14">{formatMs(entry.video_generation_ms)}</span>
            <span className="w-14 text-blue-400 font-bold">{formatMs(entry.total_ms)}</span>
          </div>
        </div>
      ))}
    </div>
  )
}

// ── Pipeline View ─────────────────────────────────────────────

// Agent order for pipeline display (processing order)
const PIPELINE_ORDER = [
  'TherapistContext', 'ContentAnalyzer', 'Orchestrator', 'KnowledgeBase', 'PharmaAdvisor',
  'Performance', 'ResponseFlow', 'VoiceAnalysis',
]

const PIPELINE_COLORS: Record<string, string> = {
  TherapistContext: 'bg-violet-500',
  ContentAnalyzer: 'bg-blue-500',
  Orchestrator: 'bg-yellow-500',
  KnowledgeBase: 'bg-amber-500',
  PharmaAdvisor: 'bg-orange-500',
  Performance: 'bg-emerald-500',
  ResponseFlow: 'bg-sky-500',
  VoiceAnalysis: 'bg-green-500',
}

interface PipelineMessage {
  messageId: number
  events: DebugEvent[]
  totalMs: number | null
}

function PipelineTab({ debugEvents }: { debugEvents: DebugEvent[] }) {
  const [expandedMsg, setExpandedMsg] = useState<number | null>(null)

  // Group events by messageId
  const messages = useMemo<PipelineMessage[]>(() => {
    const grouped = new Map<number, DebugEvent[]>()
    for (const evt of debugEvents) {
      const existing = grouped.get(evt.messageId) || []
      existing.push(evt)
      grouped.set(evt.messageId, existing)
    }
    return Array.from(grouped.entries())
      .map(([messageId, events]) => {
        const perf = events.find(e => e.agent === 'Performance')
        return {
          messageId,
          events: events.sort((a, b) => {
            const ai = PIPELINE_ORDER.indexOf(a.agent)
            const bi = PIPELINE_ORDER.indexOf(b.agent)
            return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi)
          }),
          totalMs: perf?.data.total_ms as number | null ?? null,
        }
      })
      .reverse()
  }, [debugEvents])

  if (messages.length === 0) {
    return <div className="text-neutral-600 text-xs text-center py-8">No pipeline data. Send a message to see traces.</div>
  }

  return (
    <div className="text-xs font-mono">
      {messages.map(({ messageId, events, totalMs }) => {
        const isExpanded = expandedMsg === messageId
        const ca = events.find(e => e.agent === 'ContentAnalyzer')
        const emotion = ca?.data.emotion as string | undefined
        const perf = events.find(e => e.agent === 'Performance')
        const orch = events.find(e => e.agent === 'Orchestrator')
        const therapist = orch?.data.therapist as string | undefined

        return (
          <div key={messageId} className="border-b border-neutral-800">
            {/* Message header */}
            <div
              className="px-3 py-2 flex items-center gap-2 cursor-pointer hover:bg-neutral-800/50"
              onClick={() => setExpandedMsg(isExpanded ? null : messageId)}
            >
              <span className="text-neutral-600">msg#{messageId}</span>
              {emotion && <span className="text-blue-300">{emotion}</span>}
              {therapist && <span className="text-yellow-300">→ {therapist}</span>}
              <span className="flex-1" />
              {totalMs != null && (
                <span className="text-blue-400 font-bold">{formatMs(totalMs)}</span>
              )}
              <span className="text-neutral-600 text-[10px]">{isExpanded ? '▼' : '▶'}</span>
            </div>

            {/* Pipeline stages */}
            {isExpanded && (
              <div className="px-3 pb-2 space-y-1">
                {events.map((evt) => {
                  const timingMs = evt.agent === 'Performance'
                    ? evt.data.total_ms as number | undefined
                    : evt.agent === 'ContentAnalyzer'
                    ? perf?.data.content_analysis_ms as number | undefined
                    : evt.agent === 'Orchestrator'
                    ? evt.data.latency_ms as number | undefined
                    : undefined

                  const barWidth = totalMs && timingMs
                    ? Math.max(4, Math.round((timingMs / totalMs) * 100))
                    : 0

                  const barColor = PIPELINE_COLORS[evt.agent] || 'bg-neutral-500'

                  // Build compact summary per agent
                  let summary = ''
                  if (evt.agent === 'ContentAnalyzer') {
                    summary = [evt.data.emotion, evt.data.crisis !== 'none' && evt.data.crisis].filter(Boolean).join(', ')
                  } else if (evt.agent === 'Orchestrator') {
                    const signals = evt.data.escalation_signals as Record<string, unknown> | undefined
                    const signalKeys = signals ? Object.keys(signals) : []
                    summary = [
                      evt.data.therapist,
                      evt.data.escalated && `OPUS`,
                      signalKeys.length > 0 && signalKeys.join('+'),
                    ].filter(Boolean).join(' ')
                  } else if (evt.agent === 'ResponseFlow') {
                    summary = [
                      evt.data.send_voice && 'voice',
                      evt.data.send_video && 'video',
                      evt.data.reason,
                    ].filter(Boolean).join(', ')
                  } else if (evt.agent === 'VoiceAnalysis') {
                    const mc = evt.data.mismatch_count as number | undefined
                    summary = [evt.data.emotion, mc && mc > 0 && `⚠️${mc} mismatch`].filter(Boolean).join(', ')
                  } else if (evt.agent === 'Performance') {
                    summary = '✓'
                  } else {
                    summary = JSON.stringify(evt.data).slice(0, 50)
                  }

                  return (
                    <div key={evt.id} className="flex items-center gap-2">
                      <span className="w-4 text-center">{evt.icon}</span>
                      <span className="w-28 shrink-0 truncate text-neutral-400">{evt.agent}</span>
                      {barWidth > 0 && (
                        <div className="w-20 h-2 bg-neutral-800 rounded-full overflow-hidden shrink-0">
                          <div className={`h-full rounded-full ${barColor}`} style={{ width: `${barWidth}%` }} />
                        </div>
                      )}
                      {timingMs != null && (
                        <span className="w-12 shrink-0 text-neutral-500 text-right">{formatMs(timingMs)}</span>
                      )}
                      <span className="text-neutral-300 truncate flex-1">{summary}</span>
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

// ── Help Modal ───────────────────────────────────────────────
function HelpModal({ onClose }: { onClose: () => void }) {
  const sections = [
    { key: 'WS', icon: '\u2194', desc: 'WebSocket traffic. Filter by direction (in/out) and message type. Each row is expandable to see full JSON payload.' },
    { key: 'Logs', icon: '\uD83D\uDCDC', desc: 'Python server logs streamed in real-time. Filter by level (DEBUG/INFO/WARNING/ERROR) and logger name. Errors are sorted to top and have a red border. Multi-line logs (tracebacks) are expandable.' },
    { key: 'Events', icon: '\uD83E\uDDE0', desc: 'Agent debug events: ContentAnalyzer, Orchestrator, ResponseFlow, VoiceAnalysis, etc. Filter by agent name or message ID.' },
    { key: 'Pipe', icon: '\u26A1', desc: 'Pipeline view: groups all events by message into a timeline showing processing order, timing bars, and agent decisions.' },
    { key: 'Perf', icon: '\uD83D\uDCCA', desc: 'Performance: per-message timing breakdown (analysis, LLM, TTS, video), session averages, and therapeutic metrics (emotions, crisis, escalations).' },
  ]
  const features = [
    { label: 'Search', desc: 'Global search bar filters across all columns in current tab.' },
    { label: '\u2398 Copy row', desc: 'Click the copy icon on any row to copy that entry.' },
    { label: 'Copy All', desc: 'Bottom bar "Copy" button copies all currently visible (filtered) entries as formatted text.' },
    { label: 'Clear', desc: 'Clears entries for the active tab.' },
    { label: '\uD83D\uDCCC Pin', desc: 'Auto-scroll to latest entries. Toggle ON/OFF.' },
    { label: '\uD83D\uDD34 Badge', desc: 'Error count on Logs tab. Red dot on status bar apple when errors exist.' },
  ]

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60" onClick={onClose}>
      <div
        className="bg-neutral-900 border border-neutral-700 rounded-lg w-[360px] max-h-[80vh] overflow-y-auto shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-neutral-700">
          <span className="text-blue-400 font-bold text-sm">Debug Panel Help</span>
          <button onClick={onClose} className="text-neutral-500 hover:text-neutral-200">{'\u2715'}</button>
        </div>
        <div className="px-4 py-3 space-y-3 text-xs">
          <div className="text-neutral-400 font-bold uppercase tracking-wide">Tabs</div>
          {sections.map((s) => (
            <div key={s.key}>
              <div className="text-neutral-200 font-bold">{s.icon} {s.key}</div>
              <div className="text-neutral-400 mt-0.5">{s.desc}</div>
            </div>
          ))}
          <div className="border-t border-neutral-800 pt-3 mt-3">
            <div className="text-neutral-400 font-bold uppercase tracking-wide mb-2">Features</div>
            {features.map((f) => (
              <div key={f.label} className="flex gap-2 mb-1.5">
                <span className="text-neutral-300 font-bold shrink-0">{f.label}</span>
                <span className="text-neutral-500">{f.desc}</span>
              </div>
            ))}
          </div>
          <div className="border-t border-neutral-800 pt-3 mt-3 text-neutral-600 text-[10px]">
            Toggle debug mode: Ctrl+Shift+D (cycle OFF {'\u2192'} GREEN {'\u2192'} BLUE)
          </div>
        </div>
      </div>
    </div>
  )
}

// ── Main Panel ────────────────────────────────────────────────
export default function AdvancedDebugPanel({ onClose }: { onClose: () => void }) {
  const [activeTab, setActiveTab] = useState<TabId>(() => {
    try {
      return (localStorage.getItem('janus_debug_active_tab') as TabId) || 'ws'
    } catch { return 'ws' }
  })
  const [pinScroll, setPinScroll] = useState(true)
  const [showHelp, setShowHelp] = useState(false)

  // Shared search across all tabs
  const [searchQuery, setSearchQuery] = useState('')

  // Filters
  const [wsFilters, setWsFilters] = useState<WsFilters>({ direction: 'all', typeFilter: '' })
  const [serverFilters, setServerFilters] = useState<ServerFilters>({ levels: new Set(['DEBUG', 'INFO', 'WARNING', 'ERROR']), loggerFilter: '' })
  const [debugFilters, setDebugFilters] = useState<DebugFilters>({ agentFilter: '', messageIdFilter: '' })

  // Store data
  const wsLog = useChatStore((s) => s.wsLog)
  const serverLogs = useChatStore((s) => s.serverLogs)
  const debugEvents = useChatStore((s) => s.debugEvents)
  const clearWsLog = useChatStore((s) => s.clearWsLog)
  const clearServerLogs = useChatStore((s) => s.clearServerLogs)
  const clearDebugEvents = useChatStore((s) => s.clearDebugEvents)

  const scrollRef = useRef<HTMLDivElement>(null)

  // Persist active tab
  useEffect(() => {
    try { localStorage.setItem('janus_debug_active_tab', activeTab) } catch {}
  }, [activeTab])

  // Auto-scroll
  useEffect(() => {
    if (pinScroll && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight
    }
  }, [wsLog, serverLogs, debugEvents, pinScroll, activeTab])

  // Filtered data (memoized to avoid recomputation on every render)
  const filteredWs = useMemo(() => {
    const q = searchQuery.toLowerCase()
    return wsLog.filter((e) => {
      if (wsFilters.direction !== 'all' && e.direction !== wsFilters.direction) return false
      if (wsFilters.typeFilter && !e.type.toLowerCase().includes(wsFilters.typeFilter.toLowerCase())) return false
      if (q && !e.type.toLowerCase().includes(q) && !e.summary.toLowerCase().includes(q) && !JSON.stringify(e.rawData).toLowerCase().includes(q)) return false
      return true
    })
  }, [wsLog, wsFilters, searchQuery])

  const filteredServer = useMemo(() => {
    const q = searchQuery.toLowerCase()
    const filtered = serverLogs.filter((e) => {
      if (!serverFilters.levels.has(e.level)) return false
      if (serverFilters.loggerFilter && !e.logger.toLowerCase().includes(serverFilters.loggerFilter.toLowerCase())) return false
      if (q && !e.message.toLowerCase().includes(q) && !e.logger.toLowerCase().includes(q) && !e.level.toLowerCase().includes(q)) return false
      return true
    })
    // Chronological order — no sorting by level (errors stay in-place)
    return filtered
  }, [serverLogs, serverFilters, searchQuery])

  // Error count for Logs tab badge
  const serverErrorCount = useMemo(() =>
    serverLogs.filter(e => e.level === 'ERROR').length,
    [serverLogs]
  )

  const filteredDebug = useMemo(() => {
    const q = searchQuery.toLowerCase()
    return debugEvents.filter((e) => {
      if (debugFilters.agentFilter && !e.agent.toLowerCase().includes(debugFilters.agentFilter.toLowerCase())) return false
      if (debugFilters.messageIdFilter && String(e.messageId) !== debugFilters.messageIdFilter) return false
      if (q && !e.agent.toLowerCase().includes(q) && !JSON.stringify(e.data).toLowerCase().includes(q)) return false
      return true
    })
  }, [debugEvents, debugFilters, searchQuery])

  const toggleLevel = useCallback((level: string) => {
    setServerFilters((prev) => {
      const next = new Set(prev.levels)
      if (next.has(level)) next.delete(level)
      else next.add(level)
      return { ...prev, levels: next }
    })
  }, [])

  // Performance entries from debug events
  const perfEntries = useMemo<PerfEntry[]>(() =>
    debugEvents
      .filter((e) => e.agent === 'Performance')
      .map((e) => ({
        messageId: e.messageId,
        timestamp: e.timestamp,
        content_analysis_ms: e.data.content_analysis_ms as number | undefined,
        generation_ms: e.data.generation_ms as number | undefined,
        tts_generation_ms: e.data.tts_generation_ms as number | undefined,
        tts_duration_ms: e.data.tts_duration_ms as number | undefined,
        video_generation_ms: e.data.video_generation_ms as number | undefined,
        total_ms: e.data.total_ms as number | undefined,
      })),
    [debugEvents]
  )

  const handleCopyAll = useCallback(() => {
    let text = ''
    if (activeTab === 'ws') {
      text = filteredWs.map(e => `[${formatTime(e.timestamp)}] ${e.direction === 'out' ? '\u2192' : '\u2190'} ${e.type}: ${JSON.stringify(e.rawData)}`).join('\n')
    } else if (activeTab === 'server') {
      text = filteredServer.map(e => `[${formatTime(e.timestamp)}] [${e.level}] ${e.logger}: ${e.message}`).join('\n')
    } else if (activeTab === 'debug') {
      text = filteredDebug.map(e => `[${formatTime(e.timestamp)}] ${e.agent} msg:${e.messageId} ${JSON.stringify(e.data)}`).join('\n')
    }
    if (text) copyToClipboard(text)
  }, [activeTab, filteredWs, filteredServer, filteredDebug])

  const handleClear = () => {
    if (activeTab === 'ws') clearWsLog()
    else if (activeTab === 'server') clearServerLogs()
    else clearDebugEvents()
  }

  // Pipeline: unique message IDs from debug events
  const pipelineMessageCount = useMemo(() => {
    const ids = new Set(debugEvents.map(e => e.messageId))
    return ids.size
  }, [debugEvents])

  const count = activeTab === 'ws' ? filteredWs.length
    : activeTab === 'server' ? filteredServer.length
    : activeTab === 'perf' ? perfEntries.length
    : activeTab === 'pipeline' ? pipelineMessageCount
    : filteredDebug.length

  const tabs: { id: TabId; label: string; total: number; badge?: string }[] = [
    { id: 'ws', label: 'WS', total: wsLog.length },
    { id: 'server', label: 'Logs', total: serverLogs.length, badge: serverErrorCount > 0 ? `${serverErrorCount}\uD83D\uDD34` : undefined },
    { id: 'debug', label: 'Events', total: debugEvents.length },
    { id: 'pipeline', label: 'Pipe', total: pipelineMessageCount },
    { id: 'perf', label: 'Perf', total: perfEntries.length },
  ]

  return (
    <div className="fixed right-0 top-0 h-full w-full sm:w-[400px] bg-neutral-900 text-neutral-200 shadow-2xl z-50 flex flex-col border-l border-neutral-700 animate-in slide-in-from-right duration-200" data-testid="advanced-debug-panel">
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-neutral-700 bg-neutral-900">
        <div className="flex items-center gap-2">
          <span className="text-blue-400 text-sm font-bold">Advanced Debug</span>
          <button
            onClick={() => setShowHelp(true)}
            className="text-neutral-600 hover:text-neutral-300 transition-colors text-xs"
            title="Help"
          >
            ?
          </button>
        </div>
        <button
          onClick={onClose}
          className="text-neutral-500 hover:text-neutral-200 transition-colors"
          title="Close panel"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-neutral-700">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            data-testid={`debug-tab-${tab.id}`}
            className={`flex-1 px-2 py-2 text-xs font-medium transition-colors ${
              activeTab === tab.id
                ? 'text-blue-400 border-b-2 border-blue-400 bg-neutral-800'
                : 'text-neutral-500 hover:text-neutral-300'
            }`}
          >
            {tab.label}
            {tab.badge ? (
              <span className="ml-1 text-red-400">({tab.badge})</span>
            ) : tab.total > 0 ? (
              <span className="ml-1 text-neutral-600">({tab.total})</span>
            ) : null}
          </button>
        ))}
      </div>

      {/* Search + Filters */}
      <div className="px-3 py-2 border-b border-neutral-800 bg-neutral-900/50 flex flex-wrap gap-2 items-center text-xs">
        {activeTab !== 'perf' && activeTab !== 'pipeline' && (
          <input
            type="text"
            placeholder="Search all..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="bg-neutral-800 text-neutral-300 rounded px-2 py-1 border border-neutral-700 text-xs w-full mb-1"
          />
        )}
        {activeTab === 'ws' && (
          <>
            <select
              value={wsFilters.direction}
              onChange={(e) => setWsFilters((f) => ({ ...f, direction: e.target.value as WsFilters['direction'] }))}
              className="bg-neutral-800 text-neutral-300 rounded px-2 py-1 border border-neutral-700 text-xs"
            >
              <option value="all">All</option>
              <option value="in">{'\u2190'} In</option>
              <option value="out">{'\u2192'} Out</option>
            </select>
            <input
              type="text"
              placeholder="Filter type..."
              value={wsFilters.typeFilter}
              onChange={(e) => setWsFilters((f) => ({ ...f, typeFilter: e.target.value }))}
              className="bg-neutral-800 text-neutral-300 rounded px-2 py-1 border border-neutral-700 text-xs flex-1 min-w-[80px]"
            />
          </>
        )}
        {activeTab === 'server' && (
          <>
            <div className="flex gap-1">
              {['DEBUG', 'INFO', 'WARNING', 'ERROR'].map((lvl) => (
                <button
                  key={lvl}
                  onClick={() => toggleLevel(lvl)}
                  className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${
                    serverFilters.levels.has(lvl)
                      ? `${LEVEL_COLORS[lvl]} bg-neutral-800 border border-neutral-600`
                      : 'text-neutral-600 bg-neutral-900 border border-neutral-800'
                  }`}
                >
                  {lvl.slice(0, 4)}
                </button>
              ))}
            </div>
            <input
              type="text"
              placeholder="Filter logger..."
              value={serverFilters.loggerFilter}
              onChange={(e) => setServerFilters((f) => ({ ...f, loggerFilter: e.target.value }))}
              className="bg-neutral-800 text-neutral-300 rounded px-2 py-1 border border-neutral-700 text-xs flex-1 min-w-[80px]"
            />
          </>
        )}
        {activeTab === 'debug' && (
          <>
            <input
              type="text"
              placeholder="Filter agent..."
              value={debugFilters.agentFilter}
              onChange={(e) => setDebugFilters((f) => ({ ...f, agentFilter: e.target.value }))}
              className="bg-neutral-800 text-neutral-300 rounded px-2 py-1 border border-neutral-700 text-xs flex-1 min-w-[80px]"
            />
            <input
              type="text"
              placeholder="Message ID"
              value={debugFilters.messageIdFilter}
              onChange={(e) => setDebugFilters((f) => ({ ...f, messageIdFilter: e.target.value }))}
              className="bg-neutral-800 text-neutral-300 rounded px-2 py-1 border border-neutral-700 text-xs w-20"
            />
          </>
        )}
      </div>

      {/* Event List */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto">
        {activeTab === 'ws' && (
          filteredWs.length === 0
            ? <div className="text-neutral-600 text-xs text-center py-8">No WS events captured</div>
            : filteredWs.map((e) => <WsEventRow key={e.id} entry={e} />)
        )}
        {activeTab === 'server' && (
          filteredServer.length === 0
            ? <div className="text-neutral-600 text-xs text-center py-8">No server logs received</div>
            : filteredServer.map((e) => <ServerLogRow key={e.id} entry={e} />)
        )}
        {activeTab === 'debug' && (
          filteredDebug.length === 0
            ? <div className="text-neutral-600 text-xs text-center py-8">No debug events captured</div>
            : filteredDebug.map((e) => <DebugEventRow key={e.id} entry={e} />)
        )}
        {activeTab === 'pipeline' && <PipelineTab debugEvents={debugEvents} />}
        {activeTab === 'perf' && <PerfTab perfEntries={perfEntries} debugEvents={debugEvents} />}
      </div>

      {showHelp && <HelpModal onClose={() => setShowHelp(false)} />}

      {/* Bottom Bar */}
      <div className="flex items-center justify-between px-3 py-2 border-t border-neutral-700 bg-neutral-900 text-xs">
        <div className="flex items-center gap-2">
          <button
            onClick={handleClear}
            className="text-neutral-500 hover:text-red-400 transition-colors"
            title="Clear"
          >
            Clear
          </button>
          {(activeTab === 'ws' || activeTab === 'server' || activeTab === 'debug') && (
            <>
              <span className="text-neutral-600">|</span>
              <button
                onClick={handleCopyAll}
                className="text-neutral-500 hover:text-blue-400 transition-colors"
                title="Copy all visible entries"
              >
                Copy
              </button>
            </>
          )}
          <span className="text-neutral-600">|</span>
          <span className="text-neutral-500">{count} entries</span>
        </div>
        <button
          onClick={() => setPinScroll(!pinScroll)}
          className={`transition-colors ${pinScroll ? 'text-blue-400' : 'text-neutral-600'}`}
          title={pinScroll ? 'Auto-scroll ON' : 'Auto-scroll OFF'}
        >
          {'\uD83D\uDCCC'} {pinScroll ? 'ON' : 'OFF'}
        </button>
      </div>
    </div>
  )
}
